import React, { memo, useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import ArchiveIcon from 'icons/archive.svg';
import CheckIcon from 'icons/check.svg';
import ContentCopyIcon from 'icons/content_copy.svg';
import TrashIcon from 'icons/ic-trash.svg';
import MinusIcon from 'icons/minus.svg';
import SortAppliedChangeIcon from 'icons/sort-applied-change.svg';
import SortAppliedIcon from 'icons/sort-applied.svg';
import SortReversedChangeIcon from 'icons/sort-reversed-change.svg';
import SortReversedIcon from 'icons/sort-reversed.svg';
import SortUnsortChangeIcon from 'icons/sort-unsort-change.svg';
import { enableAllPlugins, produce } from 'immer';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { usePopper } from 'react-popper';
import tailwindConfig from 'stylesheets/tailwind.config';
import voca from 'voca';

import useOutsideClick from 'components/hooks/useOutsideClick';
import usePrevious from 'components/hooks/usePrevious';
import useSize from 'components/hooks/useSize';

import Checkbox from 'components/application/Checkbox';
import OptionsDropdown from 'components/application/options/OptionsDropdown';
import OptionsItem from 'components/application/options/OptionsItem';
import OptionsToggle from 'components/application/options/OptionsToggle';
import DotSkeleton from 'components/application/tableSkeletons/DotSkeleton';
import DotTextSkeleton from 'components/application/tableSkeletons/DotTextSkeleton';
import TextSkeleton from 'components/application/tableSkeletons/TextSkeleton';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectsDashboardContext } from 'components/contexts/ProjectsDashboardContext';
import ApprovalStatusIndicator from 'components/projects/components/ApprovalStatusIndicator';
import LifecycleStatusIndicator from 'components/projects/components/LifecycleStatusIndicator';
import ReviewStatusIndicator from 'components/projects/components/ReviewStatusIndicator';
import RiskAssessmentsStatusIndicator from 'components/projects/components/RiskAssessmentsStatusIndicator';
import SignatureStatusIndicator from 'components/projects/components/SignatureStatusIndicator';
import { dashboardConfigurableFieldKey } from 'components/projectsDashboard/helpers';
import useDocumentAllowance from 'components/projectsDashboard/hooks/useDocumentAllowance';

import { asFormattedDateTime, formatDate } from 'components/helpers/dates';
import { resourceShape } from 'components/helpers/serialisableResources';
import { personDisplayName } from 'components/helpers/users';

const { colors } = tailwindConfig.theme;

enableAllPlugins();

export default function ProjectsTable({
  columnOrder,
  columnSizing,
  columnSort,
  columnVisibility,
  columns,
  domainDashboardReport,
  folderId,
  isDataPending,
  isProjectDuplicationBlocked,
  onColumnSizingChange,
  onColumnSortChange,
  onDocumentAllowanceLimitReached,
  onProjectActionsClick,
  onProjectArchiveClick,
  onProjectDestroyClick,
  onProjectDuplicateClick,
  onProjectToggle,
  onSelectAllProjectsToggle,
  selectedProjectIds,
}) {
  const {
    division: {
      attributes: { singularRAMSName },
    },
  } = useCurrentActor();

  const {
    projectPolicy,
    projectsArchivePolicy,
    projectsDuplicationProcessPolicy,
  } = useProjectsDashboardContext();

  const wrapperRef = useRef();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectsDashboard.components.ProjectsTable',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const domainDashboardReportProjects = useMemo(() => {
    const projects =
      domainDashboardReport?.data.relationships.projects.data || [];

    return isDataPending ?
        Array(projects.length || 25).fill({})
      : projects.map(
          (project) => domainDashboardReport.included.project[project.id],
        );
  }, [isDataPending, domainDashboardReport]);

  const domainDashboardReportIncluded = useMemo(
    () => domainDashboardReport?.included || {},
    [domainDashboardReport],
  );

  const projectFieldIndexedByOriginalField = useMemo(() => {
    const projects =
      domainDashboardReport?.included?.project ?
        Object.values(domainDashboardReport.included.project)
      : [];

    const fields = domainDashboardReport?.included?.field || {};

    return projects.reduce((projectMap, project) => {
      projectMap[project.id] =
        project.relationships.dashboardConfigurableFields?.data.reduce(
          (fieldMap, dashboardConfigurableField) => {
            const field = fields[dashboardConfigurableField.id];
            fieldMap[field.relationships.originalField.data.id] = field;

            return fieldMap;
          },
          {},
        );

      return projectMap;
    }, {});
  }, [domainDashboardReport]);

  const table = useReactTable({
    data: domainDashboardReportProjects,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',
    onColumnSizingChange,
    state: {
      columnOrder:
        folderId ?
          columnOrder.filter((columnId) => columnId !== 'folder')
        : columnOrder,
      columnSizing,
      columnVisibility:
        folderId ? { ...columnVisibility, folder: false } : columnVisibility,
    },
    initialState: {
      columnPinning: {
        left: ['selector', 'live', 'project'],
        right: ['actions'],
      },
    },
    meta: {
      columnSort,
      included: domainDashboardReportIncluded,
      isDataPending,
      isProjectDuplicationBlocked,
      isSortEnabled: selectedProjectIds.length === 0,
      onColumnSortChange,
      onDocumentAllowanceLimitReached,
      onProjectActionsClick,
      onProjectArchiveClick,
      onProjectDestroyClick,
      onProjectDuplicateClick,
      onProjectToggle,
      onSelectAllProjectsToggle,
      projectFieldIndexedByOriginalField,
      projectPolicy,
      projectsArchivePolicy,
      projectsDuplicationProcessPolicy,
      selectedProjectIds,
      translations: {
        actions: {
          archive: i18nTShared('archiveName', {
            interpolation: { escapeValue: false },
            name: singularRAMSName,
          }),
          delete: i18nTShared('deleteName', {
            interpolation: { escapeValue: false },
            name: singularRAMSName,
          }),
          duplicate: i18nTShared('duplicateName', {
            interpolation: { escapeValue: false },
            name: singularRAMSName,
          }),
          duplicateDisabledTooltip: i18nT('actions.duplicate.disabledTooltip', {
            interpolation: { escapeValue: false },
            singularRAMSName,
          }),
        },
        deletedUser: i18nTShared('deletedUser'),
        deselect: i18nTShared('deselect'),
        deselectAll: i18nTShared('deselectAll'),
        select: i18nTShared('select'),
        selectAll: i18nTShared('selectAll'),
      },
      wrapperRef,
    },
  });

  const tableColumnSizeVars = useMemo(
    () =>
      table.getFlatHeaders().reduce((acc, header) => {
        acc[`--col-${header.column.id}-size`] = header.column.getSize();

        return acc;
      }, {}),
    [
      table.getState().columnOrder,
      table.getState().columnSizingInfo,
      table.getState().columnSizing,
      table.getState().columnVisibility,
    ],
  );

  const { width: wrapperWidth = 0 } = useSize(wrapperRef);
  const tableWidth = table.getTotalSize();
  const bufferWidth = Math.max(0, wrapperWidth - tableWidth);

  useLayoutEffect(() => {
    if (wrapperWidth) {
      table.setColumnSizing(buildColumnResizing({ table, wrapperWidth }));
    }
  }, [table, wrapperWidth]);

  return (
    <div ref={wrapperRef}>
      <div className='tw-relative tw-w-full tw-overflow-y-auto'>
        <table
          className='tw-table-fixed tw-border-separate tw-select-none tw-border-1 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-grey-100 tw-bg-white'
          style={{ ...tableColumnSizeVars, width: tableWidth }}
        >
          <ProjectsTableHead bufferWidth={bufferWidth} table={table} />
          {table.getState().columnSizingInfo.isResizingColumn ?
            <MemoizedProjectsTableBody
              bufferWidth={bufferWidth}
              table={table}
            />
          : <ProjectsTableBody bufferWidth={bufferWidth} table={table} />}
        </table>
      </div>
    </div>
  );
}

ProjectsTable.propTypes = {
  columnOrder: PropTypes.array.isRequired,
  columnSizing: PropTypes.object.isRequired,
  columnSort: PropTypes.object.isRequired,
  columnVisibility: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  domainDashboardReport: PropTypes.shape({
    data: resourceShape('dashboardReport'),
    included: PropTypes.object,
  }),
  folderId: PropTypes.string,
  isDataPending: PropTypes.bool.isRequired,
  isProjectDuplicationBlocked: PropTypes.bool,
  onColumnSizingChange: PropTypes.func.isRequired,
  onColumnSortChange: PropTypes.func.isRequired,
  onDocumentAllowanceLimitReached: PropTypes.func.isRequired,
  onProjectActionsClick: PropTypes.func.isRequired,
  onProjectArchiveClick: PropTypes.func.isRequired,
  onProjectDestroyClick: PropTypes.func.isRequired,
  onProjectDuplicateClick: PropTypes.func.isRequired,
  onProjectToggle: PropTypes.func.isRequired,
  onSelectAllProjectsToggle: PropTypes.func.isRequired,
  selectedProjectIds: PropTypes.arrayOf(PropTypes.string).isRequired,
};

function buildColumnResizing({ table, wrapperWidth }) {
  return table.getFlatHeaders().reduce((acc, header) => {
    if (
      header.column.id === 'project' &&
      table.getLeftTotalSize() > wrapperWidth / 2
    ) {
      acc[header.column.id] =
        header.column.getSize() - (table.getLeftTotalSize() - wrapperWidth / 2);
    } else {
      acc[header.column.id] = header.column.getSize();
    }
    return acc;
  }, {});
}

function ProjectsTableHead({ bufferWidth, table }) {
  const className =
    table.options.meta.selectedProjectIds.length > 0 ?
      'tw-bg-blue-050'
    : 'tw-bg-grey-050';

  return (
    <thead>
      {table.getHeaderGroups().map((headerGroup, rowIndex) => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map((header) => {
            const headerCell = (
              <th
                className={className}
                key={header.id}
                style={buildColumnStyle({
                  column: header.column,
                  isPinnedColumnVerticalLineVisible: bufferWidth === 0,
                  rowIndex,
                })}
              >
                <div className='tw-relative'>
                  <div className='tw-truncate tw-text-s tw-font-medium tw-tracking-auto tw-text-grey-900'>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </div>
                  {header.column.getCanResize() && (
                    <div
                      className='tw-group/resize-wrapper tw-absolute tw-right-0 tw-top-0 tw-h-[42px] tw-w-2 tw-cursor-col-resize'
                      data-element-name='resize-handle'
                    >
                      <div
                        className='tw-hidden tw-h-[42px] tw-border-0 tw-border-r-2 tw-border-solid tw-border-blue-500 group-hover/resize-wrapper:tw-block'
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                      />
                    </div>
                  )}
                </div>
              </th>
            );

            if (!header.column.getIsLastColumn()) return headerCell;

            const bufferHeaderCell = (
              <th
                className={className}
                key='buffer'
                style={{ width: bufferWidth }}
              />
            );

            return header.column.getIsPinned() === 'right' ?
                [bufferHeaderCell, headerCell]
              : [headerCell, bufferHeaderCell];
          })}
        </tr>
      ))}
    </thead>
  );
}

function buildColumnStyle({
  column,
  isPinnedColumnVerticalLineVisible,
  rowIndex,
}) {
  const isPinned = column.getIsPinned();

  const isLastLeftPinnedColumnVerticalLineVisible =
    isPinned === 'left' &&
    column.getIsLastColumn('left') &&
    isPinnedColumnVerticalLineVisible;

  const isFirstRightPinnedColumnVerticalLineVisible =
    isPinned === 'right' &&
    column.getIsFirstColumn('right') &&
    isPinnedColumnVerticalLineVisible;

  return {
    boxShadow:
      isLastLeftPinnedColumnVerticalLineVisible ?
        `-2px 0 ${colors.grey[100]} inset`
      : isFirstRightPinnedColumnVerticalLineVisible ?
        `2px 0 ${colors.grey[100]} inset`
      : undefined,
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    position: isPinned ? 'sticky' : 'relative',
    width: `calc(var(--col-${column?.id}-size) * 1px)`,
    zIndex: isPinned ? 999 - rowIndex : 0,
  };
}

function ProjectsTableBody({ bufferWidth, table }) {
  const { selectedProjectIds } = table.options.meta;

  return (
    <tbody>
      {table.getRowModel().rows.map((row, rowIndex) => (
        <tr className='tw-group/row' key={row.id}>
          {row.getVisibleCells().map((cell) => {
            const className = classNames(
              'tw-h-13 tw-border-0 tw-border-t-1 tw-border-solid tw-border-grey-100',
              selectedProjectIds.includes(row.original.id) ? 'tw-bg-blue-025'
              : 'tw-bg-white hover:tw-bg-blue-025 group-hover/row:tw-bg-blue-025',
            );

            const dataCell = (
              <td
                className={className}
                style={buildColumnStyle({
                  column: cell.column,
                  isPinnedColumnVerticalLineVisible: bufferWidth === 0,
                  rowIndex,
                })}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            );

            if (!cell.column.getIsLastColumn()) return dataCell;

            const bufferDataCell = (
              <td
                className={className}
                key='buffer'
                style={{ width: bufferWidth }}
              />
            );

            return cell.column.getIsPinned() === 'right' ?
                [bufferDataCell, dataCell]
              : [dataCell, bufferDataCell];
          })}
        </tr>
      ))}
    </tbody>
  );
}

const MemoizedProjectsTableBody = memo(
  ProjectsTableBody,
  (prev, next) =>
    prev.bufferWidth === next.bufferWidth &&
    prev.table.options.data === next.table.options.data,
);

export function ProjectsTableHeader({
  columnId,
  columnName,
  columnSort,
  isSortEnabled,
  isSortVisible = true,
  onColumnSortChange,
}) {
  const handleMouseLeave = () => {
    if (!columnSort.isFutureDirectionShownOnHover) {
      onColumnSortChange({
        ...columnSort,
        isFutureDirectionShownOnHover: true,
      });
    }
  };

  const handleSortIconClick = ({ direction, isUnsorted = false }) => {
    onColumnSortChange(
      isUnsorted ?
        {
          columnId: 'lastUpdated',
          direction: 'applied',
          isFutureDirectionShownOnHover: true,
          isUnsorted: true,
        }
      : {
          columnId,
          direction,
          isFutureDirectionShownOnHover: false,
          isUnsorted: false,
        },
    );
  };

  return (
    <div
      className={classNames(
        'tw-group/container tw-flex tw-h-10 tw-items-center tw-justify-start tw-border-1 tw-border-solid tw-border-transparent tw-bg-transparent tw-p-2',
        isSortEnabled &&
          'tw-cursor-pointer hover:tw-border-blue-300 hover:tw-bg-blue-025',
      )}
      onMouseLeave={handleMouseLeave}
    >
      <div className='tw-truncate'>{columnName}</div>
      {isSortVisible && (
        <div
          className={classNames(
            'tw-flex tw-w-full tw-max-w-[28px] tw-justify-end',
            isSortEnabled && 'group-hover/container:tw-hidden',
          )}
        >
          {(() => {
            if (
              columnSort.columnId === columnId &&
              columnSort.direction === 'applied'
            ) {
              return (
                <SortAppliedIcon
                  data-element-name='sort-applied-icon'
                  height={24}
                  width={24}
                />
              );
            } else if (
              columnSort.columnId === columnId &&
              columnSort.direction === 'reversed'
            ) {
              return (
                <SortReversedIcon
                  data-element-name='sort-reversed-icon'
                  height={24}
                  width={24}
                />
              );
            } else {
              return null;
            }
          })()}
        </div>
      )}
      {isSortEnabled &&
        (columnSort.columnId === columnId ||
          columnSort.isFutureDirectionShownOnHover) && (
          <div className='tw-hidden tw-w-full tw-max-w-[28px] tw-justify-end group-hover/container:tw-flex'>
            <div className='tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-sm hover:tw-bg-blue-050'>
              {(() => {
                if (columnSort.columnId === columnId) {
                  if (columnSort.direction === 'applied') {
                    return columnSort.isFutureDirectionShownOnHover ?
                        <SortReversedChangeIcon
                          data-element-name='sort-reversed-change-icon'
                          height={24}
                          onClick={() => {
                            handleSortIconClick({ direction: 'reversed' });
                          }}
                          width={24}
                        />
                      : <SortAppliedIcon
                          data-element-name='sort-applied-icon'
                          height={24}
                          onClick={() => {
                            handleSortIconClick({ direction: 'reversed' });
                          }}
                          width={24}
                        />;
                  } else if (columnSort.direction === 'reversed') {
                    return (
                      columnSort.isFutureDirectionShownOnHover ?
                        columnId === 'lastUpdated' ?
                          <SortAppliedChangeIcon
                            data-element-name='sort-applied-change-icon'
                            height={24}
                            onClick={() =>
                              handleSortIconClick({ direction: 'applied' })
                            }
                            width={24}
                          />
                        : <SortUnsortChangeIcon
                            data-element-name='sort-unsort-change-icon'
                            height={24}
                            onClick={() =>
                              handleSortIconClick({ isUnsorted: true })
                            }
                            width={24}
                          />
                      : <SortReversedIcon
                          data-element-name='sort-reversed-icon'
                          height={24}
                          onClick={() =>
                            handleSortIconClick(
                              columnId === 'lastUpdated' ?
                                { direction: 'applied' }
                              : { isUnsorted: true },
                            )
                          }
                          width={24}
                        />
                    );
                  }
                } else if (columnSort.isFutureDirectionShownOnHover) {
                  return (
                    <SortAppliedChangeIcon
                      data-element-name='sort-applied-change-icon'
                      height={24}
                      onClick={() => {
                        handleSortIconClick({ direction: 'applied' });
                      }}
                      width={24}
                    />
                  );
                }
              })()}
            </div>
          </div>
        )}
    </div>
  );
}

function ProjectsTableRowActions({ row, table }) {
  if (table.options.meta.isDataPending) return null;

  const {
    isProjectDuplicationBlocked,
    onDocumentAllowanceLimitReached,
    onProjectActionsClick,
    onProjectArchiveClick,
    onProjectDestroyClick,
    onProjectDuplicateClick,
    projectPolicy,
    projectsArchivePolicy,
    projectsDuplicationProcessPolicy,
    selectedProjectIds,
    translations,
    wrapperRef,
  } = table.options.meta;

  const optionsRef = useRef();
  const optionsDropdownRef = useRef();

  const [isOptionsDropdownVisible, setIsOptionsDropdownVisible] =
    useState(false);

  const prevIsOptionsDropdownVisible = usePrevious(isOptionsDropdownVisible);

  const [popperReferenceRef, setPopperReferenceRef] = useState(null);
  const [popperRef, setPopperRef] = useState(null);

  const { attributes, styles } = usePopper(popperReferenceRef, popperRef, {
    placement: 'bottom-end',
    modifiers: [
      { name: 'offset', enabled: true, options: { offset: [0, 3] } },
      { name: 'flip', options: { fallbackPlacements: [] } },
    ],
  });

  const [optionsDropdownInViewRef, isOptionsDropdownInView] = useInView();

  function handleRowOptionsToggleClick() {
    setIsOptionsDropdownVisible((prev) => !prev);
  }

  const { handleDocumentAllowanceCheck, isDocumentAllowanceCheckPending } =
    useDocumentAllowance({
      onLimitNotReached: () => onProjectDuplicateClick({ id: row.original.id }),
      onLimitReached: onDocumentAllowanceLimitReached,
    });

  const areActionsShown =
    selectedProjectIds.length === 0 ||
    (selectedProjectIds.length === 1 &&
      selectedProjectIds[0] === row.original.id);

  if (!prevIsOptionsDropdownVisible && isOptionsDropdownVisible) {
    onProjectActionsClick({ id: row.original.id });
  }

  useOutsideClick(() => {
    setIsOptionsDropdownVisible(false);
  }, [optionsRef, optionsDropdownRef]);

  return (
    areActionsShown && (
      <div
        className='tw-p-2'
        data-element-name='projects-dashboard-row-options'
        ref={optionsRef}
      >
        <OptionsToggle
          buttonRef={setPopperReferenceRef}
          isOptionsDropdownVisible={isOptionsDropdownVisible}
          onClick={handleRowOptionsToggleClick}
          threeDotsIconClassName='[&_path]:group-hover/row:tw-fill-grey-700'
        />
        <div ref={optionsDropdownInViewRef}>
          {wrapperRef.current ?
            createPortal(
              <div ref={optionsDropdownRef}>
                <div
                  className='tw-z-[9999]'
                  ref={setPopperRef}
                  style={styles.popper}
                  {...attributes.popper}
                >
                  {isOptionsDropdownVisible && isOptionsDropdownInView && (
                    <OptionsDropdown>
                      {projectsDuplicationProcessPolicy.canCreate && (
                        <OptionsItem
                          isDisabled={
                            isDocumentAllowanceCheckPending ||
                            isProjectDuplicationBlocked
                          }
                          onClick={() => {
                            setIsOptionsDropdownVisible(false);
                            handleDocumentAllowanceCheck();
                          }}
                          wrapper={(children) => (
                            <Tooltip
                              className='tooltip-dark--max-w-xxs'
                              placement='left'
                              tooltip={
                                translations.actions.duplicateDisabledTooltip
                              }
                              trigger='hover'
                            >
                              {children}
                            </Tooltip>
                          )}
                          wrapperCondition={isProjectDuplicationBlocked}
                        >
                          <div className='tw-flex tw-gap-2'>
                            <ContentCopyIcon
                              className={classNames(
                                'tw-flex-none',
                                (
                                  isDocumentAllowanceCheckPending ||
                                    isProjectDuplicationBlocked
                                ) ?
                                  '[&_path]:tw-fill-grey-300'
                                : '[&_path]:tw-fill-grey-700',
                              )}
                              height={20}
                              width={20}
                            />
                            {translations.actions.duplicate}
                          </div>
                        </OptionsItem>
                      )}
                      {projectsArchivePolicy.canCreate && (
                        <OptionsItem
                          onClick={() => {
                            setIsOptionsDropdownVisible(false);
                            onProjectArchiveClick({ id: row.original.id });
                          }}
                        >
                          <div className='tw-flex tw-gap-2'>
                            <ArchiveIcon
                              className='[&_path]:tw-fill-grey-700'
                              height={20}
                              width={20}
                            />
                            {translations.actions.archive}
                          </div>
                        </OptionsItem>
                      )}
                      {projectPolicy.canDestroy && (
                        <OptionsItem
                          isDangerous={true}
                          onClick={() => {
                            setIsOptionsDropdownVisible(false);
                            onProjectDestroyClick({ id: row.original.id });
                          }}
                        >
                          <div className='tw-flex tw-gap-2'>
                            <TrashIcon
                              className='[&_path]:tw-fill-grey-700 [&_path]:group-hover:tw-fill-red-700'
                              height={20}
                              width={20}
                            />
                            {translations.actions.delete}
                          </div>
                        </OptionsItem>
                      )}
                    </OptionsDropdown>
                  )}
                </div>
              </div>,
              wrapperRef.current,
            )
          : null}
        </div>
      </div>
    )
  );
}

export function buildColumnNames({
  dashboardConfigurableFields,
  projectReferenceLabel,
  singularRAMSName,
  translations,
}) {
  const columnNames = {
    live: translations.live,
    project: voca.capitalize(singularRAMSName),
    folder: translations.folder,
    reference: projectReferenceLabel || translations.reference,
    lastUpdated: translations.lastUpdated,
    riskAssessments: translations.riskAssessments,
    reviewDate: translations.reviewDate,
    approvalStatus: translations.approvalStatus,
    creator: translations.creator,
    creationDate: translations.creationDate,
    startDate: translations.startDate,
    reviewer: translations.reviewer,
    signatureStatus: translations.signatureStatus,
    approver: translations.approver,
  };

  dashboardConfigurableFields.forEach((dashboardConfigurableField) => {
    const columnId = dashboardConfigurableFieldKey({
      dashboardConfigurableField,
    });

    columnNames[columnId] = voca.capitalize(
      dashboardConfigurableField.attributes.label,
    );
  });

  return columnNames;
}

export function buildProjectTableConfiguration({
  canAction,
  canSelect,
  dashboardConfigurableFields,
  isApprovalConfigurable,
  isFolderConfigurable,
  isReviewConfigurable,
  isRiskAssessmentConfigurable,
  isSignatureConfigurable,
  projectReferenceLabel,
  singularRAMSName,
  tableColumnConfigurations = [],
  tableSortConfiguration = null,
  translations,
}) {
  const columnNames = buildColumnNames({
    dashboardConfigurableFields,
    projectReferenceLabel,
    singularRAMSName,
    translations,
  });

  const hiddenColumns = new Set([
    ...[
      !canAction && 'actions',
      !canSelect && 'selector',
      !isApprovalConfigurable && ['approvalStatus', 'approver'],
      !isFolderConfigurable && 'folder',
      !isReviewConfigurable && ['reviewDate', 'reviewer'],
      !isRiskAssessmentConfigurable && 'riskAssessments',
      !isSignatureConfigurable && 'signatureStatus',
    ]
      .flat()
      .filter(Boolean),
  ]);

  const formattedTableColumnConfigurations = tableColumnConfigurations.reduce(
    (acc, tableColumnConfiguration) => {
      const columnName = tableColumnConfiguration.attributes.columnType;

      const columnId =
        columnName === 'dashboard_configurable_field' ?
          dashboardConfigurableFieldKey({
            dashboardConfigurableField:
              tableColumnConfiguration.relationships.field.data,
          })
        : voca.camelCase(columnName);

      acc.set(columnId, tableColumnConfiguration.attributes.width);

      return acc;
    },
    new Map(),
  );

  const formattedTableSortConfiguration =
    tableSortConfiguration &&
    (() => {
      const columnName = tableSortConfiguration.attributes.columnType;

      const columnId =
        columnName === 'dashboard_configurable_field' ?
          dashboardConfigurableFieldKey({
            dashboardConfigurableField:
              tableSortConfiguration.relationships.field.data,
          })
        : voca.camelCase(columnName);

      return {
        columnId,
        direction: tableSortConfiguration.attributes.direction,
      };
    })();

  return produce({ isReset: false }, (draftState) => {
    assignColumnOrder({ draftState });
    assignColumnVisibility({ draftState });
    assignColumnSort({ draftState });
    assignColumnSizingDefaults({ draftState });
    assignColumnSizing({ draftState });
    assignColumns({ draftState });
  });

  function assignColumnOrder({ draftState }) {
    let columnOrder = [
      'selector',
      'live',
      'project',
      'folder',
      'reference',
      'lastUpdated',
      'riskAssessments',
      'reviewDate',
      'approvalStatus',
      'creator',
      'creationDate',
      'startDate',
      'reviewer',
      'signatureStatus',
      'approver',
      'actions',
    ];

    dashboardConfigurableFields.forEach((dashboardConfigurableField) => {
      const columnId = dashboardConfigurableFieldKey({
        dashboardConfigurableField,
      });

      const reorderInsertColumnIndex = columnOrder.indexOf('folder') + 1;

      columnOrder = [
        ...columnOrder.slice(0, reorderInsertColumnIndex),
        columnId,
        ...columnOrder.slice(reorderInsertColumnIndex),
      ];
    });

    if (formattedTableColumnConfigurations.size > 0) {
      const reorderInsertColumnIndex = columnOrder.indexOf('project') + 1;

      columnOrder = [
        ...new Set([
          ...columnOrder.slice(0, reorderInsertColumnIndex),
          ...formattedTableColumnConfigurations.keys(),
          ...columnOrder.slice(reorderInsertColumnIndex),
        ]),
      ];
    }

    draftState.columnOrder = columnOrder.filter(
      (column) => !hiddenColumns.has(column),
    );
  }

  function assignColumnVisibility({ draftState }) {
    let columnVisibility = {
      selector: true,
      live: true,
      project: true,
      folder: false,
      reference: false,
      lastUpdated: false,
      riskAssessments: false,
      reviewDate: false,
      approvalStatus: false,
      creator: false,
      creationDate: false,
      startDate: false,
      reviewer: false,
      signatureStatus: false,
      approver: false,
      actions: true,
    };

    if (formattedTableColumnConfigurations.size > 0) {
      dashboardConfigurableFields.forEach((dashboardConfigurableField) => {
        const columnId = dashboardConfigurableFieldKey({
          dashboardConfigurableField,
        });

        columnVisibility[columnId] = false;
      });

      [...formattedTableColumnConfigurations.keys()].forEach((columnId) => {
        columnVisibility[columnId] = true;
      });
    } else {
      dashboardConfigurableFields.forEach((dashboardConfigurableField) => {
        const columnId = dashboardConfigurableFieldKey({
          dashboardConfigurableField,
        });

        columnVisibility[columnId] = true;
      });

      columnVisibility = {
        ...columnVisibility,
        ...{
          folder: true,
          reference: true,
          lastUpdated: true,
          riskAssessments: true,
          reviewDate: true,
          approvalStatus: true,
        },
      };
    }

    draftState.columnVisibility = Object.keys(columnVisibility)
      .filter((column) => !hiddenColumns.has(column))
      .reduce(
        (obj, column) => ({ ...obj, [column]: columnVisibility[column] }),
        {},
      );
  }

  function assignColumnSort({ draftState }) {
    const columnSort = {
      columnId: 'lastUpdated',
      direction: 'applied',
      isFutureDirectionShownOnHover: true,
      isUnsorted: true,
    };

    if (
      formattedTableSortConfiguration &&
      !hiddenColumns.has(formattedTableSortConfiguration.columnId)
    ) {
      columnSort.columnId = formattedTableSortConfiguration.columnId;
      columnSort.direction = formattedTableSortConfiguration.direction;
      columnSort.isUnsorted = false;
    }

    draftState.columnSort = columnSort;
  }

  function assignColumnSizingDefaults({ draftState }) {
    const columnSizingDefaults = {
      selector: { size: 40, minSize: 40, maxSize: 40 },
      live: { size: 42, minSize: 42, maxSize: 42 },
      project: { size: 560, minSize: 42, maxSize: 560 },
      folder: { size: 120, minSize: 42, maxSize: 560 },
      reference: { size: 112, minSize: 42, maxSize: 560 },
      lastUpdated: { size: 144, minSize: 42, maxSize: 280 },
      riskAssessments: { size: 152, minSize: 42, maxSize: 280 },
      reviewDate: { size: 144, minSize: 42, maxSize: 280 },
      approvalStatus: { size: 116, minSize: 88, maxSize: 280 },
      creator: { size: 120, minSize: 42, maxSize: 560 },
      creationDate: { size: 144, minSize: 42, maxSize: 280 },
      startDate: { size: 144, minSize: 42, maxSize: 280 },
      reviewer: { size: 120, minSize: 42, maxSize: 560 },
      signatureStatus: { size: 144, minSize: 42, maxSize: 280 },
      approver: { size: 120, minSize: 42, maxSize: 560 },
      actions: { size: 50, minSize: 50, maxSize: 50 },
    };

    dashboardConfigurableFields.forEach((dashboardConfigurableField) => {
      const columnId = dashboardConfigurableFieldKey({
        dashboardConfigurableField,
      });

      columnSizingDefaults[columnId] = { size: 120, minSize: 42, maxSize: 560 };
    });

    draftState.columnSizingDefaults = columnSizingDefaults;
  }

  function assignColumnSizing({ draftState }) {
    draftState.columnSizing = Object.keys(
      draftState.columnSizingDefaults,
    ).reduce((acc, columnId) => {
      if (draftState.columnVisibility[columnId])
        acc[columnId] =
          formattedTableColumnConfigurations.get(columnId) ||
          draftState.columnSizingDefaults[columnId].size;

      return acc;
    }, {});
  }

  function assignColumns({ draftState }) {
    function buildColumnSizingByColumnId(columnId) {
      return {
        size:
          draftState.columnSizing[columnId] ||
          draftState.columnSizingDefaults[columnId].size,
        minSize: draftState.columnSizingDefaults[columnId].minSize,
        maxSize: draftState.columnSizingDefaults[columnId].maxSize,
      };
    }

    const columns = [
      {
        id: 'selector',
        header: ({ table }) => {
          if (table.options.meta.isDataPending) return null;

          const projectIds = table.options.data.map((project) => project.id);

          const { selectedProjectIds } = table.options.meta;

          const areProjectsSelected = selectedProjectIds.length > 0;

          const areAllProjectsSelected = projectIds.every((projectId) =>
            selectedProjectIds.includes(projectId),
          );

          return (
            <div className='tw-p-2 tw-pl-4'>
              <Checkbox
                checkedIcon={areAllProjectsSelected ? CheckIcon : MinusIcon}
                isChecked={areProjectsSelected}
                label={
                  areProjectsSelected ?
                    table.options.meta.translations.deselectAll
                  : table.options.meta.translations.selectAll
                }
                name='select-all-projects'
                onChange={() =>
                  table.options.meta.onSelectAllProjectsToggle({ projectIds })
                }
              />
            </div>
          );
        },
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return null;

          const { id } = row.original;

          const isSelected = table.options.meta.selectedProjectIds.includes(id);

          return (
            <div className='tw-flex tw-h-full tw-items-center tw-pl-4 tw-pr-2 hover:tw-bg-blue-050'>
              <Checkbox
                isChecked={isSelected}
                label={
                  isSelected ?
                    table.options.meta.translations.deselect
                  : table.options.meta.translations.select
                }
                name='select-project'
                onChange={() => table.options.meta.onProjectToggle({ id })}
              />
            </div>
          );
        },
        enableResizing: false,
        ...buildColumnSizingByColumnId('selector'),
      },
      {
        id: 'live',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            isSortVisible={false}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <DotSkeleton />;

          const status = _.camelCase(
            row.original.meta.calculatedLifecycleStatus,
          );

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-flex tw-h-full tw-items-center tw-justify-center tw-px-2'>
                <LifecycleStatusIndicator status={status} />
              </div>
            </a>
          );
        },
        enableResizing: false,
        ...buildColumnSizingByColumnId('live'),
      },
      {
        id: 'project',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <TextSkeleton />;

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-900'>
                {row.original.attributes.name}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('project'),
      },
      {
        id: 'folder',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <TextSkeleton />;

          const folder =
            table.options.meta.included.folder[
              row.original.relationships.folder?.data?.id
            ];

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
                {folder?.attributes.name}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('folder'),
      },
      {
        id: 'reference',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <TextSkeleton />;

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
                {row.original.attributes.reference}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('reference'),
      },
      {
        id: 'lastUpdated',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <TextSkeleton />;

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
                {asFormattedDateTime({
                  date: row.original.attributes.modified,
                })}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('lastUpdated'),
      },
      {
        id: 'riskAssessments',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <DotTextSkeleton />;

          const status = _.camelCase(
            row.original.meta.calculatedRiskAssessmentsStatus,
          );

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-flex tw-h-full tw-items-center tw-justify-start tw-px-2'>
                {status && status !== 'noStatus' && (
                  <RiskAssessmentsStatusIndicator status={status} />
                )}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('riskAssessments'),
      },
      {
        id: 'reviewDate',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <DotTextSkeleton />;

          const scheduledContentReviewRequest =
            table.options.meta.included.projectContentReviewRequest[
              row.original.relationships.scheduledContentReviewRequest?.data?.id
            ];

          const { dueDate } = scheduledContentReviewRequest?.attributes || {};

          const status = _.camelCase(row.original.meta.calculatedReviewStatus);

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-flex tw-h-full tw-items-center tw-justify-start tw-px-2'>
                {status && status !== 'noStatus' && (
                  <ReviewStatusIndicator
                    dueDate={
                      dueDate &&
                      formatDate({ date: dueDate, format: 'dd MMM yy' })
                    }
                    status={status}
                  />
                )}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('reviewDate'),
      },
      {
        id: 'approvalStatus',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <TextSkeleton />;

          const status = _.camelCase(row.original.meta.approvalStatus);

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-flex tw-h-full tw-items-center tw-justify-start tw-px-2'>
                {status && <ApprovalStatusIndicator status={status} />}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('approvalStatus'),
      },
      {
        id: 'creator',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <TextSkeleton />;

          const creator =
            table.options.meta.included.user[
              row.original.relationships.user?.data?.id
            ];

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
                {creator &&
                  [
                    personDisplayName({ attributes: creator.attributes }),
                    creator.attributes.deletedAt &&
                      table.options.meta.translations.deletedUser,
                  ]
                    .filter(Boolean)
                    .join(' ')}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('creator'),
      },
      {
        id: 'creationDate',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <TextSkeleton />;

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
                {asFormattedDateTime({
                  date: row.original.attributes.createdAt,
                })}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('creationDate'),
      },
      {
        id: 'startDate',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <TextSkeleton />;

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
                {row.original.attributes.startDate &&
                  asFormattedDateTime({
                    date: row.original.attributes.startDate,
                  })}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('startDate'),
      },
      {
        id: 'reviewer',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <TextSkeleton />;

          const scheduledContentReviewRequest =
            table.options.meta.included.projectContentReviewRequest[
              row.original.relationships.scheduledContentReviewRequest?.data?.id
            ];

          const reviewer =
            table.options.meta.included.user[
              scheduledContentReviewRequest?.relationships?.assignee?.data?.id
            ];

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
                {reviewer &&
                  [
                    personDisplayName({ attributes: reviewer.attributes }),
                    reviewer.attributes.deletedAt &&
                      table.options.meta.translations.deletedUser,
                  ]
                    .filter(Boolean)
                    .join(' ')}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('reviewer'),
      },
      {
        id: 'signatureStatus',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <DotTextSkeleton />;

          const status = _.camelCase(
            row.original.meta.calculatedSignatureStatus,
          );

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-flex tw-h-full tw-items-center tw-justify-start tw-px-2'>
                {status && status !== 'noStatus' && (
                  <SignatureStatusIndicator status={status} />
                )}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('signatureStatus'),
      },
      {
        id: 'approver',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <TextSkeleton />;

          const approver =
            table.options.meta.included.user[
              row.original.relationships.calculatedApprover?.data?.id
            ];

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
                {approver &&
                  [
                    personDisplayName({ attributes: approver.attributes }),
                    approver.attributes.deletedAt &&
                      table.options.meta.translations.deletedUser,
                  ]
                    .filter(Boolean)
                    .join(' ')}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('approver'),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row, table }) => (
          <ProjectsTableRowActions row={row} table={table} />
        ),
        enableResizing: false,
        ...buildColumnSizingByColumnId('actions'),
      },
    ];

    dashboardConfigurableFields.forEach((originalField) => {
      const columnId = dashboardConfigurableFieldKey({
        dashboardConfigurableField: originalField,
      });

      columns.push({
        id: columnId,
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <TextSkeleton />;

          const projectField =
            table.options.meta.projectFieldIndexedByOriginalField[
              row.original.id
            ]?.[originalField.id];

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
                {projectField?.attributes.value}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId(columnId),
      });
    });

    draftState.columns = columns.filter(
      (column) => !hiddenColumns.has(column.id),
    );
  }
}
